import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const methods = [
  {
    icon: 'ri-settings-4-line',
    title: '环境重构',
    subtitle: 'Environment',
    desc: '把为人工准备的研发环境改造成为 AI 准备的研发环境。',
    bullets: [
      'CLAUDE.md 项目知识入口与术语统一',
      'Skills / Rules / Agents 三层规范',
      '业务规则反推为 AI 可读文档',
      '检测 + 发布链路接成可自动化闭环',
    ],
    dark: true,
  },
  {
    icon: 'ri-user-star-line',
    title: '实战带教',
    subtitle: 'Hands-on',
    desc: '在你们自己真实的、还在迭代的项目里把它一次性跑通。',
    bullets: [
      '不开沙盒、不用 Demo、不写空中楼阁',
      '架构师 / 工程师 / 测试多角色同时跑通',
      '现场处理真实边界与判断冲突',
      '逐步把主导权交还给团队',
    ],
    dark: false,
  },
  {
    icon: 'ri-database-2-line',
    title: '资产沉淀',
    subtitle: 'Assets',
    desc: '驻场结束时把可复用的资产原样交付给团队。',
    bullets: [
      'Skills / Rules / Agents 完整配置',
      '工作流脚本与流水线规范',
      '业务规则与 ADR 文档体系',
      '可迁移到下一个项目的清单',
    ],
    dark: false,
  },
];

export default function MethodSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
          <div
            className="transition-all duration-700"
            style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)' }}
          >
            <span className="text-xs uppercase tracking-widest text-[#64748B] mb-4 block" style={{ fontFamily: 'Inter, sans-serif' }}>
              Our Approach
            </span>
            <h2
              className="text-3xl md:text-5xl font-bold text-[#0F172A] leading-tight"
              style={{ fontFamily: '\'Noto Sans SC\', sans-serif', letterSpacing: '-0.02em' }}
            >
              不是增加工具，
              <br />
              而是重构能力
            </h2>
          </div>
          <div
            className="max-w-md transition-all duration-700"
            style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)', transitionDelay: '150ms' }}
          >
            <p className="text-base text-[#64748B] leading-relaxed" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>
              我们的方法论基于一个核心判断：AI 协作能力不是工具使用技巧，而是一种需要系统性建立的工程能力。
            </p>
          </div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {methods.map((m, idx) => (
            <div
              key={idx}
              className={`rounded-2xl p-10 flex flex-col gap-6 transition-all duration-700 ${
                m.dark ? 'bg-[#0F172A] text-white' : 'bg-[#F8FAFC] border border-slate-100'
              }`}
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(30px)',
                transitionDelay: `${idx * 150 + 300}ms`,
              }}
            >
              <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${m.dark ? 'bg-white/10' : 'bg-[#0F172A]/5'}`}>
                <i className={`${m.icon} text-xl ${m.dark ? 'text-white' : 'text-[#0F172A]'}`} />
              </div>
              <div>
                <h3
                  className={`text-xl font-bold mb-1 ${m.dark ? 'text-white' : 'text-[#0F172A]'}`}
                  style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                >
                  {m.title}
                </h3>
                <p
                  className={`text-xs mb-4 ${m.dark ? 'text-white/40' : 'text-[#64748B]'}`}
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {m.subtitle}
                </p>
                <p
                  className={`text-sm leading-relaxed mb-5 ${m.dark ? 'text-white/60' : 'text-[#64748B]'}`}
                  style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                >
                  {m.desc}
                </p>
                <ul className="flex flex-col gap-2.5">
                  {m.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <i
                        className={`ri-check-line text-sm mt-0.5 shrink-0 ${m.dark ? 'text-[#F97316]' : 'text-[#2563EB]'}`}
                      />
                      <span
                        className={`text-xs leading-relaxed ${m.dark ? 'text-white/70' : 'text-[#475569]'}`}
                        style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                      >
                        {b}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-auto">
                <Link
                  to="/training"
                  className={`inline-flex items-center gap-1.5 text-sm font-medium cursor-pointer transition-colors whitespace-nowrap ${
                    m.dark ? 'text-white/60 hover:text-white' : 'text-[#0F172A] hover:text-[#2563EB]'
                  }`}
                  style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                >
                  了解更多
                  <i className="ri-arrow-right-line text-sm" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
